// Draft-localization language helpers, shared by the search-creation selector,
// the cover-letter "Regenerate in…" picker, and the reply "Rewrite in…" picker.
//
// The backend owns ALL language logic; the frontend only supplies an optional
// preference and renders the language metadata the backend returns. See
// docs reference: localization-frontend-integration.md.

// Sentinel meaning "let the backend auto-decide" (capability-gated). Omitting a
// value entirely also defers to the next level down; this is the explicit form.
export const AUTO_LANGUAGE = 'auto';

// Human display names for the language codes the backend knows about. This is a
// *display* convenience for populating the pickers — NOT an acceptance whitelist.
// The backend accepts any valid 2-letter ISO 639-1 code and, for codes outside
// this map, echoes the uppercased code as the name (e.g. "ko" -> "KO"). Chips
// therefore always render the `languageName` the backend returns, never a value
// mapped here.
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  pt: 'Portuguese',
  pl: 'Polish',
  sv: 'Swedish',
  da: 'Danish',
  no: 'Norwegian',
  fi: 'Finnish',
  he: 'Hebrew',
  ru: 'Russian',
  ja: 'Japanese',
  zh: 'Chinese',
};

export interface LanguageOption {
  label: string;
  value: string;
}

// "Auto (recommended)" first, then the named languages sorted alphabetically.
// The picker only ever emits "auto" or a 2-letter code from this list, so no
// client value can violate the backend rule (auto | ^[a-z]{2}$).
export const LANGUAGE_PICKER_OPTIONS: LanguageOption[] = [
  { label: 'Auto (recommended)', value: AUTO_LANGUAGE },
  ...Object.entries(LANGUAGE_NAMES)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];
